import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion, useInView } from 'framer-motion';
import { Shield, Cpu, HardDrive } from 'lucide-react';
import { DottedLogo } from './DottedLogo';
import { useCopy } from './copy/copyMode';
// Helper to type out command text character by character
function TypewriterText({
  text
}: {
  text: string;
}) {
  const [chars, setChars] = useState(0);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (reducedMotion) {
      setChars(text.length);
      return;
    }
    setChars(0);
    const interval = setInterval(() => {
      setChars(c => {
        if (c >= text.length) {
          clearInterval(interval);
          return c;
        }
        return c + 1;
      });
    }, 30); // 30ms per character
    return () => clearInterval(interval);
  }, [text, reducedMotion]);
  return <span data-id="element-540">{text.slice(0, chars)}</span>;
}
// Terminal lines to animate
const TERMINAL_LINES = [{
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 0
}, {
  text: 'giga browser export --site billing.portal',
  type: 'command',
  delay: 400
}, {
  text: 'Restored authenticated browser session',
  type: 'output',
  delay: 2000
}, {
  text: 'Downloaded 184 invoices to ./downloads/invoices/',
  type: 'output',
  delay: 2500
}, {
  text: '',
  type: 'empty',
  delay: 2600
}, {
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 2900
}, {
  text: 'python reconcile.py',
  type: 'command',
  delay: 3300
}, {
  text: '✓ Reconciled totals · report.pdf ready',
  type: 'success',
  delay: 4300
}, {
  text: '',
  type: 'empty',
  delay: 4400
}, {
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 4700
}];
export function ComputerUse() {
  const copy = useCopy();
  const reducedMotion = useReducedMotion();
  const terminalRef = useRef<HTMLDivElement>(null);
  // Only kick off the typing sequence once the terminal scrolls into view
  const inView = useInView(terminalRef, {
    once: true,
    margin: '-120px'
  });
  const [visibleLines, setVisibleLines] = useState<number>(reducedMotion ? TERMINAL_LINES.length : 0);
  useEffect(() => {
    if (reducedMotion) {
      setVisibleLines(TERMINAL_LINES.length);
      return;
    }
    if (!inView) return;
    setVisibleLines(0);
    let timeouts: NodeJS.Timeout[] = [];
    TERMINAL_LINES.forEach((line, index) => {
      const timeout = setTimeout(() => {
        setVisibleLines(index + 1);
      }, line.delay);
      timeouts.push(timeout);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [reducedMotion, inView]);
  return <>
      {/* DESKTOP LAYOUT (Old Version) */}
      <section className="hidden md:block relative w-full bg-[#FAF8F4] py-32 overflow-hidden" data-id="element-541">
        <DottedLogo variant="light" opacity={0.4} className="top-0 -left-64 w-[800px] h-[800px]" data-id="element-542" />

        <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-90" data-id="element-543">
          <video src="https://cdn.magicpatterns.com/uploads/9rEPiEudH1JJSQ7e9xrKPb/hf_20260616_083352_929e6c52-e5b2-425a-882f-180098bd55b1.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover object-[20%_center] grayscale" data-id="element-544" />
        </div>

        <div className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-545" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-10 flex justify-end" data-id="element-546">
          <div className="w-[55%] xl:w-[50%] flex flex-col gap-16 relative pt-12 pr-12" data-id="element-547">
            <div className="absolute top-[50%] -left-[25%] xl:-left-[30%] w-[25%] xl:w-[30%] h-[15%] pointer-events-none z-0" data-id="element-548">
              <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" className="overflow-visible" data-id="element-549">
                <path d="M0,100 L100,0" stroke="#D4D0C8" strokeWidth="1.5" strokeDasharray="4 4" fill="none" data-id="element-550" />
                <circle cx="100" cy="0" r="3" fill="#D4D0C8" data-id="element-551" />
              </svg>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-552">
              <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-6" data-id="element-553">
                Under the hood
              </span>
              <h2 className="text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] mb-6" data-id="element-554">
                {copy.computerUse.headline}
              </h2>
              <p className="text-xl text-neutral-500 leading-relaxed max-w-xl" data-id="element-555">
                Every task runs on Giga's own secure machine in the cloud — it
                writes code, crunches files, and opens a browser when work lives
                behind a login.
              </p>
            </motion.div>

            <motion.div ref={terminalRef} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="relative max-w-2xl z-10 w-full" data-id="element-556">
              <div className="w-full rounded-2xl bg-[#1C1510] border border-white/10 shadow-2xl overflow-hidden flex flex-col font-mono text-sm" data-id="element-557">
                {/* Window Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#241B14]" data-id="element-558">
                  <div className="flex items-center gap-2 w-24" data-id="element-559">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F56]" data-id="element-560" />
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-561" />
                    <div className="w-3 h-3 rounded-full bg-[#27C93F]" data-id="element-562" />
                  </div>
                  <div className="flex items-center gap-2 text-[#9C8E82] text-xs font-medium truncate" data-id="element-563">
                    <Cpu size={12} className="opacity-70 shrink-0 w-3.5 h-3.5" data-id="element-564" />
                    <span className="truncate" data-id="element-565">
                      agent-7f32 · isolated sandbox
                    </span>
                  </div>
                  <div className="w-24 flex justify-end" data-id="element-566">
                    <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-[#F97316]/30 bg-[#F97316]/10 text-[#F97316] text-[10px] font-bold tracking-wider uppercase" data-id="element-567">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" data-id="element-568" />
                      LIVE
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center px-4 border-b border-white/[0.06] bg-[#241B14] text-xs" data-id="element-569">
                  <div className="px-4 py-2 border-b-2 border-[#F97316] text-white font-medium" data-id="element-570">
                    Terminal
                  </div>
                  <div className="px-4 py-2 text-[#9C8E82] font-medium" data-id="element-571">
                    Files
                  </div>
                  <div className="px-4 py-2 text-[#9C8E82] font-medium" data-id="element-572">
                    Browser
                  </div>
                </div>

                {/* Terminal Body */}
                <div className="p-6 min-h-[240px] bg-[#1C1510] text-[#E7E0D7] overflow-hidden" data-id="element-573">
                  <div className="flex flex-col gap-1.5 w-full whitespace-pre-wrap" data-id="element-574">
                    {TERMINAL_LINES.slice(0, visibleLines).map((line, i) => {
                    if (line.type === 'empty') return <div key={i} className="h-4" data-id="element-575" />;
                    const isLastVisible = i === visibleLines - 1;
                    const isPrompt = line.type === 'prompt';
                    const isCommand = line.type === 'command';
                    const isSuccess = line.type === 'success';
                    if (isPrompt) {
                      const nextLine = TERMINAL_LINES[i + 1];
                      const hasCommand = nextLine && nextLine.type === 'command' && visibleLines > i + 1;
                      const isCommandLastVisible = hasCommand && visibleLines === i + 2;
                      return <div key={i} className="flex items-center gap-2" data-id="element-576">
                            <span className="text-[#F97316]" data-id="element-577">{line.text}</span>
                            {hasCommand ? <div className="flex items-center gap-0.5" data-id="element-578">
                                <span className="text-[#FAF8F4]" data-id="element-579">
                                  <TypewriterText text={nextLine.text} data-id="element-580" />
                                </span>
                                {isCommandLastVisible && <span className="w-2 h-4 bg-[#F97316] animate-pulse" data-id="element-581" />}
                              </div> : isLastVisible && <span className="w-2 h-4 bg-[#F97316] animate-pulse" data-id="element-582" />}
                          </div>;
                    }
                    if (isCommand) return null;
                    if (isSuccess) return <div key={i} className="text-[#F97316] font-semibold" data-id="element-583">
                            {line.text}
                          </div>;
                    return <div key={i} className="text-[#9C8E82]" data-id="element-584">
                          {line.text}
                        </div>;
                  })}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06] bg-[#241B14] text-xs text-[#9C8E82]" data-id="element-585">
                  <div className="flex items-center gap-4" data-id="element-586">
                    <div className="flex items-center gap-1.5" data-id="element-587">
                      <Cpu size={12} className="w-3.5 h-3.5" data-id="element-588" />2 vCPU
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-589">
                      <HardDrive size={12} className="w-3.5 h-3.5" data-id="element-590" />
                      14.8 GB free
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#E7E0D7]" data-id="element-591">
                    <Shield size={12} className="w-3.5 h-3.5" data-id="element-592" />
                    isolated
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MOBILE LAYOUT (New Version) */}
      <section className="md:hidden relative w-full bg-[#F5F5F5] py-12 overflow-hidden" data-id="element-593">
        <div className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-594" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 flex flex-col items-start" data-id="element-595">
          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="w-full mb-4" data-id="element-596">
            <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5" data-id="element-597">
              Under the hood
            </span>
            <h2 className="text-4xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A]" data-id="element-598">
              {copy.computerUse.headline}
            </h2>
          </motion.div>

          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: reducedMotion ? 0 : 0.2
        }} className="w-full flex justify-center mb-6" data-id="element-599">
            <video src="https://cdn.magicpatterns.com/uploads/nHYUuideA9ns1K5r8TEarm/hf_20260617_104816_63075aa5-0f21-43f6-812f-d1a0ae5be9f7%20(1).mp4" autoPlay loop muted playsInline className="w-full max-w-[600px] object-contain" data-id="element-600" />
          </motion.div>

          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: reducedMotion ? 0 : 0.4
        }} className="w-full" data-id="element-601">
            <p className="text-xl text-neutral-500 leading-relaxed font-medium" data-id="element-602">
              Every task runs on Giga's own secure machine in the cloud — it
              writes code, crunches files, and opens a browser when work lives
              behind a login.
            </p>
          </motion.div>
        </div>
      </section>
    </>;
}